import { stockfishAssetUrls } from "@/pages/analysis/engine/stockfishAssets";
import { parseStockfishInfo } from "@/pages/analysis/engine/stockfishProtocol";
import type {
  StockfishClientEvent,
  StockfishEvaluation,
  StockfishPv,
  StockfishStatus,
} from "@/pages/analysis/engine/stockfishTypes";

type Listener = (event: StockfishClientEvent) => void;

type StockfishWorkerClientOptions = {
  depth?: number;
  multiPv?: number;
};

export class StockfishWorkerClient {
  private worker: Worker | null = null;
  private listeners = new Set<Listener>();
  private status: StockfishStatus = "idle";
  private currentFen: string | null = null;
  private pendingFen: string | null = null;
  private pvs = new Map<number, StockfishPv>();
  private hasRetriedLocalWorker = false;
  private isSearching = false;
  private readonly depth: number;
  private readonly multiPv: number;

  constructor(options: StockfishWorkerClientOptions = {}) {
    this.depth = options.depth ?? 14;
    this.multiPv = options.multiPv ?? 3;
  }

  subscribe(listener: Listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  start() {
    if (this.worker || this.status === "loading" || this.status === "ready") {
      return;
    }

    if (typeof Worker === "undefined" || typeof WebAssembly === "undefined") {
      this.setStatus("unsupported", "Browser does not support Web Worker WASM.");
      return;
    }

    this.setStatus("loading");

    try {
      this.worker = new Worker(stockfishAssetUrls.worker);
      this.worker.addEventListener("message", this.onMessage);
      this.worker.addEventListener("error", this.onError);
      this.send("uci");
    } catch (error) {
      this.setStatus(
        "error",
        error instanceof Error ? error.message : "Failed to start Stockfish.",
      );
    }
  }

  analyze(fen: string) {
    this.pendingFen = fen;
    if (this.status === "idle") {
      this.start();
      return;
    }
    if (this.status === "loading") {
      return;
    }
    if (!this.worker || this.status === "error" || this.status === "unsupported") {
      return;
    }

    if (this.isSearching) {
      this.setStatus("calculating");
      return;
    }

    this.runPendingPosition();
  }

  destroy() {
    this.releaseWorker();
    this.status = "idle";
    this.currentFen = null;
    this.pendingFen = null;
    this.pvs.clear();
    this.isSearching = false;
    this.hasRetriedLocalWorker = false;
  }

  private onMessage = (event: MessageEvent) => {
    const line = String(event.data ?? "");

    if (line === "uciok") {
      this.configure();
      return;
    }

    if (line === "readyok") {
      this.setStatus("ready");
      if (this.pendingFen) {
        this.runPendingPosition();
      }
      return;
    }

    if (line.startsWith("info ")) {
      const parsed = parseStockfishInfo(line);
      if (parsed && this.currentFen) {
        this.applyPv(parsed);
      }
      return;
    }

    if (line.startsWith("bestmove")) {
      if (this.currentFen) {
        this.isSearching = false;
        this.emitEvaluation("ready");
        if (this.pendingFen && this.pendingFen !== this.currentFen) {
          this.runPendingPosition();
        } else {
          this.pendingFen = null;
        }
      }
    }
  };

  private onError = (event: ErrorEvent) => {
    this.recoverFromWorkerFailure(event.message || "Stockfish worker failed.");
  };

  private recoverFromWorkerFailure(message: string) {
    const fenToRetry = this.currentFen ?? this.pendingFen;

    if (!this.hasRetriedLocalWorker) {
      this.hasRetriedLocalWorker = true;
      this.releaseWorker();
      this.status = "idle";
      this.currentFen = null;
      this.pendingFen = fenToRetry;
      this.pvs.clear();
      this.isSearching = false;
      this.start();
      return;
    }

    this.releaseWorker();
    this.currentFen = null;
    this.pendingFen = fenToRetry;
    this.pvs.clear();
    this.isSearching = false;
    this.setStatus("error", message);
  }

  private releaseWorker() {
    this.worker?.removeEventListener("message", this.onMessage);
    this.worker?.removeEventListener("error", this.onError);
    this.worker?.terminate();
    this.worker = null;
  }

  private configure() {
    this.send("setoption name UCI_ShowWDL value true");
    this.send(`setoption name MultiPV value ${this.multiPv}`);
    this.send("setoption name Hash value 64");
    this.send("isready");
  }

  private runPendingPosition() {
    const fen = this.pendingFen;
    if (!fen || !this.worker) {
      return;
    }

    this.pendingFen = null;
    this.currentFen = fen;
    this.pvs.clear();
    this.isSearching = true;
    this.setStatus("calculating");
    this.emitEvaluation("calculating");
    this.send(`position fen ${fen}`);
    this.send(`go depth ${this.depth}`);
  }

  private applyPv(parsed: StockfishPv) {
    const existing = this.pvs.get(parsed.multipv);
    if (!existing || parsed.depth >= existing.depth) {
      this.pvs.set(parsed.multipv, parsed);
      this.emitEvaluation("calculating");
    }
  }

  private emitEvaluation(status: StockfishStatus) {
    if (!this.currentFen) {
      return;
    }
    const pvs = [...this.pvs.values()].sort((a, b) => a.multipv - b.multipv);
    const evaluation: StockfishEvaluation = {
      fen: this.currentFen,
      status,
      depth: pvs.reduce((maxDepth, pv) => Math.max(maxDepth, pv.depth), 0),
      pvs,
    };
    this.emit({ type: "evaluation", evaluation });
  }

  private setStatus(status: StockfishStatus, error?: string) {
    this.status = status;
    this.emit({ type: "status", status, error });
  }

  private send(command: string) {
    this.worker?.postMessage(command);
  }

  private emit(event: StockfishClientEvent) {
    for (const listener of this.listeners) {
      listener(event);
    }
  }
}
